import { useMutation } from "@tanstack/react-query";
import { z } from "zod";

import { PaymentApiError } from "~/features/payment/utils/payment-error-mapper.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { postAndValidate } from "~/shared/services/api/fetch-client.ts";

const getErrorMessage = (error: unknown): string => {
	if (typeof error === "string") {
		return error;
	}

	if (error && typeof error === "object") {
		const typedError = error as { message?: unknown };
		if (typeof typedError.message === "string") {
			return typedError.message;
		}
	}

	return "Failed to save payment method.";
};

export const postSubmitPayment = async (
	payload: unknown,
	signal?: AbortSignal,
): Promise<unknown> => {
	const resp = await postAndValidate(
		apiRoutes.POSTSUBMITPAYMENT,
		payload,
		z.unknown(),
		{
			signal,
		},
	);

	if (resp.success) {
		return resp.data;
	}

	// Preserve the structured HTTP status (and raw details) on a typed error
	// so downstream mapping to user-safe copy can classify the failure
	// correctly instead of falling back to a generic message.
	throw new PaymentApiError(
		getErrorMessage(resp.error),
		resp.status,
		resp.details,
	);
};

export const usePostSubmitPayment = () => {
	const { mutate, mutateAsync, isPending, error, data } = useMutation({
		mutationFn: (payload: unknown) => postSubmitPayment(payload),
	});

	return {
		submitPayment: mutate,
		submitPaymentAsync: mutateAsync,
		isSubmitting: isPending,
		error,
		data,
	};
};
